import { computeBackoffDelay } from '@ns/resilience';
import { attachCause, isHttpError, isHttpRequestError } from './errors';
import type { EndedBy } from './deadline';
import { observe } from './handlers';
import { usableDelay } from './limits';
import { DEFAULT_RETRYABLE_STATUSES } from './retry-config';
import { retryAfterMs } from './status-failure';
import type {
  AttemptStartedEvent,
  Policy,
  RetryConfig,
  RetryScheduledEvent,
} from './types';

/**
 * Whether another attempt happens, and how long the loop waits for it.
 *
 * The first half runs through two gates. Budget first, then the failure itself.
 * The budget calls nothing the caller wrote, and that is what fixes the order:
 * `shouldRetry` is the caller's own function, so asking it for an answer the
 * budget has already settled is a side effect nobody asked for.
 *
 * The second half has an order of its own, and it is the whole design: what the
 * SERVER asked for beats what we would compute, and the DEADLINE stands above
 * both.
 */

/** What the loop needs to know about the attempt that just failed. */
export interface FailedAttempt {
  /** 1-based, so `attempt >= maxAttempts` reads as "that was the last one". */
  readonly attempt: number;
  readonly config: RetryConfig;
  readonly failure: unknown;
}

/**
 * The two retry observers, and both go through `observe`.
 *
 * `observe` rather than a `try`/`catch` of their own, because the rule is the
 * one the `after` list already states (AF7) and a second spelling of it is the
 * one that later forgets a case. What they throw is caught and dropped: an
 * observer that can end the loop would turn a retryable 503 into a report
 * about the caller's own logging, and that is exactly the difference between
 * these and `shouldRetry`, whose throw does end the call.
 *
 * `url` is the best known so far, the same value `failureFor` reports on. On
 * the first attempt that is the caller's own; from the second it is the
 * address the previous attempt resolved to.
 */
export function notifyAttemptStarted(
  config: RetryConfig,
  attempt: number,
  url: string,
): void {
  const { onAttempt } = config;
  if (onAttempt === undefined) return;
  observe<AttemptStartedEvent>([onAttempt], {
    attempt,
    of: config.maxAttempts,
    scope: 'retry',
    url,
  });
}

/**
 * Fires once the DEADLINE has let the wait through, and before the wait is
 * served.
 *
 * That is what makes it count decisions rather than traffic: a wait that no
 * longer fits the deadline ends the call on this attempt, so nothing was
 * scheduled and nothing is reported. And announcing the repeat before serving
 * the pause is what makes `delayMs` worth carrying: a caller logging "again in
 * 42 ms" wants it at the start of those 42 ms, not at the end.
 *
 * EVERY EDGE IS ASKED FOR HERE, and `endedBy` is what asks all of them at once.
 * Until 2026-09-10 only the deadline was, through the `worthWaiting` line in
 * `request.ts`, and a caller who hung up DURING an attempt got an event for a
 * repeat that never went out. MEASURED against `RC15`, which says this fires
 * once less than `onAttempt`: on that path it fired an equal number of times,
 * one attempt and one announcement, with a single request on the wire.
 *
 * The two conditions are the same condition. An expired deadline and an aborted
 * caller both mean the call is over, and announcing a repeat is a promise the
 * loop cannot keep either way.
 *
 * `delayMs` is the wait that was actually taken, handed in rather than asked
 * for a second time. `retryDelayMsOf` draws jitter, so a second call would report
 * a delay the loop never waited.
 */
export function notifyRetryScheduled(
  failed: FailedAttempt,
  delayMs: number,
  url: string,
  endedBy: EndedBy | undefined,
): void {
  // TWO REASONS TO SAY NOTHING, and they are one condition. The call is already
  // over, or nobody asked. Reaching this line means the deadline still had room
  // -- `worthWaiting` threw otherwise -- so what `endedBy` catches here is the
  // caller's own signal (R03).
  const { onRetry } = failed.config;
  if (endedBy !== undefined || onRetry === undefined) return;
  observe<RetryScheduledEvent>([onRetry], {
    attempt: failed.attempt,
    delayMs,
    error: failed.failure,
    scope: 'retry',
    url,
  });
}

/**
 * The config the loop runs on, or nothing when there is no loop.
 *
 * What the value IS decides, not which properties it happens to carry (RC26): a
 * policy factory may hang metadata on the function it returns, and a check for
 * `'maxAttempts' in retry` would read that metadata as a config. A `Policy`
 * takes the retry position over entirely, so there is no config here to run.
 */
export function retryConfigOf(
  retry: RetryConfig | Policy | undefined,
): RetryConfig | undefined {
  if (retry === undefined || typeof retry === 'function') return undefined;
  return retry;
}

/**
 * The caller's OWN loop, or nothing when the built-in one runs.
 *
 * The other half of the same split, and it lives beside its twin on purpose:
 * `typeof retry === 'function'` is the one discriminant this union has, and a
 * second copy in the file that composes the call is the copy that drifts. Read
 * together, the pair says the whole rule -- a function is a loop, anything else
 * is a config, and never both.
 *
 * Without this half the union was declared and unreachable: the config reader
 * answered `undefined` for a function, which switched the built-in loop OFF, and
 * nothing invoked the function. One attempt, no retrying, while the caller
 * believed they had installed a loop -- worse than a no-op, because it removed
 * the loop that was there.
 */
export function retryPolicyOf(
  retry: RetryConfig | Policy | undefined,
): Policy | undefined {
  return typeof retry === 'function' ? retry : undefined;
}

/**
 * Whether this FAILURE is one to repeat on.
 *
 * A predicate that throws answers no, and its error is attached to the original
 * failure instead of replacing it. The costs are unequal and that decides it:
 * reading a throw as "retry" would repeat a write on the word of code that is
 * demonstrably not working, and letting the error escape would hand the caller a
 * puzzle in place of the HTTP failure that actually happened.
 */
function worthRepeating(failure: unknown, config: RetryConfig): boolean {
  const { shouldRetry } = config;
  if (shouldRetry !== undefined) {
    try {
      return shouldRetry(failure);
    } catch (predicateError) {
      attachCause(failure, predicateError);
      return false;
    }
  }

  // THE KNOWN OPT-IN (RC83). A network error has no status, so no list of
  // statuses can select it, and this is the one switch that does. It adds to
  // the default judgement rather than replacing it, so the statuses below still
  // come from the caller's own list (RC85). Read after `shouldRetry` on purpose:
  // where the type did not stop both being set, the function decides alone
  // (RC87).
  if (
    config.networkErrors === true &&
    isHttpError(failure) &&
    failure.failureKind === 'network'
  ) {
    return true;
  }

  // THE ONE STATUSLESS FAILURE THE DEFAULT JUDGEMENT REPEATS (RC53). Bounding
  // one attempt is pointless if the loop then gives up on the bound: a server
  // that accepts the connection and goes silent would still cost the whole
  // call, only faster. Every other statusless failure keeps the rule below --
  // a refused connection and an elapsed CALL deadline say nothing a second
  // attempt could improve on, and that is why this needed a class of its own
  // rather than sharing `RequestTimeoutError` (ER73).
  //
  // Read off the KIND rather than with `instanceof`, for the reason `errors.ts`
  // gives about realms, and it is the same judgement either way: `ER74` holds
  // the class-to-kind mapping total, and `FK11` promises this is exactly what a
  // caller's own `shouldRetry` may decide on.
  if (isHttpError(failure) && failure.failureKind === 'attempt-timeout') {
    return true;
  }

  // Only a failure that carries a status can be in a list of statuses. A
  // network error, a timeout and a caller abort have none, so the default list
  // says nothing about them and they go out on the first answer.
  if (!isHttpRequestError(failure)) return false;
  const statuses = config.retryableStatuses ?? DEFAULT_RETRYABLE_STATUSES;
  return statuses.includes(failure.status);
}

/**
 * The two gates, in the order that makes them real.
 *
 * BUDGET FIRST. `maxAttempts` is a total rather than a count of repeats, so the
 * last attempt is `attempt === maxAttempts` and there is nothing left to decide
 * after it. Asking the caller's predicate anyway would fire an observable side
 * effect for an answer that cannot be used.
 *
 * THE METHOD IS NOT A GATE HERE, and that is a decision rather than an omission
 * (RY12). The loop follows the caller: `retry` is opt-in, it can only be set per
 * call -- the process-wide surface has none, and a global handler that sets one
 * is refused (BE38) -- so whoever wrote `method: 'POST'` beside a `retry` wrote
 * both. Refusing anyway was a SILENT no-op of exactly the shape BE38 condemns:
 * three attempts configured, one made, nothing said. What it protected against
 * is real and still is, so it is written down where the caller reads it (RC13,
 * RC49) rather than enforced where only some of them are covered -- the gate
 * never applied to a `Policy` in the first place (RY5).
 */
export function mayRetry(failed: FailedAttempt): boolean {
  const { attempt, config, failure } = failed;
  if (attempt >= config.maxAttempts) return false;
  return worthRepeating(failure, config);
}

/**
 * What the server asked us to wait, in ms, or nothing when it asked for nothing.
 *
 * Read off the response headers rather than off `HttpThrottleError.retryAfterMs`,
 * because that field exists only on the 429 branch while the header is valid on
 * every retryable status there is -- a 503 with a maintenance window says it the
 * same way. `retryAfterMs` is that one parser, so both routes read the header
 * the same way -- including the shapes it reads more loosely than the spec
 * does. Only a value it can make no number of yields nothing, and nothing is
 * what falls through to the computed wait.
 */
function requestedWaitMsOf(failure: unknown): number | undefined {
  if (!isHttpRequestError(failure)) return undefined;
  const { headers } = failure;
  return headers === undefined ? undefined : retryAfterMs(headers);
}

/**
 * How long to wait before the next attempt.
 *
 * THE SERVER WINS WHEN IT SPOKE. A ceiling does not cap it, and that is a
 * correction rather than an oversight (RC23): Microsoft Graph documents that a
 * rejected call still accrues against the usage limits, so retrying before the
 * window is worse than useless -- it lengthens the throttling it was trying to
 * escape. A ceiling bounds what WE compute; what the server asked for is
 * honoured as asked, and it is the DEADLINE that stops an absurd one (RC33,
 * RC64).
 *
 * BELOW THAT, THE SHAPE OF `backoff` DECIDES, and `computeBackoffDelay` from
 * `@ns/resilience` works it out: a number is that wait, a list is those waits
 * in order, and an object or unset draws with full jitter under a doubling
 * ceiling, rounded up. A non-HTTP retry from `@ns/resilience` computes its
 * waits with the same function, so the two share one implementation. A non-empty
 * list is exactly as long as there are gaps, because `validateOptions` refuses
 * every other length before anything is sent (RC59).
 */
export function retryDelayMsOf(failed: FailedAttempt): number {
  const requestedWaitMs = requestedWaitMsOf(failed.failure);
  if (requestedWaitMs !== undefined) return requestedWaitMs;

  return computeBackoffDelay(failed.config.backoff, failed.attempt);
}

/**
 * Whether a wait is worth starting at all, given what is left of the deadline.
 *
 * A wait that runs past the deadline is NOT shortened, because a shortened wait
 * asks again inside the window the server just closed. It is either started or
 * skipped, and skipping hands the caller the real failure now instead of a
 * timeout later.
 *
 * THE LINE IS THE DEADLINE ITSELF. A wait that overshoots by three seconds is on
 * the wrong side of it just as surely as the `Retry-After: 300` against two
 * minutes of RC33, and a more forgiving line -- skip only once the overshoot
 * outgrows what is left -- takes away the reason for skipping at all (RC38).
 * What the caller would buy for the rest of their deadline is a
 * `RequestTimeoutError` in place of the status failure already in hand, which
 * says less about what went wrong and says it later.
 *
 * THE DEADLINE IS NOT A FLOOR, which is why there are two conditions and not
 * one. `remaining()` answers `Infinity` while the clock is unarmed, and
 * everything fits under `Infinity` -- so on its own this said yes to a wait
 * above 2^31 ms, the one Node's timer clamps to 1 ms, turning the pause into
 * the immediate retry of RC23 (RC43). `usableDelay` is that floor, and it is the
 * SAME predicate `validateOptions` refuses an oversized number in `backoff` by
 * (RC67), rather than a ceiling written a second time here (RC39).
 *
 * THE FLOOR IS BELT OVER BRACES AND IS NOT REACHED TODAY, and this note is here
 * so nobody mistakes it for tested ground. The braces are that the clock is
 * unarmed only for an unusable `timeout`, and UR21 now refuses such a call
 * before the first attempt, so `remaining()` inside the loop is a real number
 * bounded by the 120 s default. MEASURED both ways: dropping `usableDelay` from
 * this line leaves all 997 tests green, and a `Retry-After` of 999999999 s
 * behaves identically with and without it -- one attempt either way, because
 * the default deadline already turns that wait down. The floor stays for the
 * day the validation moves, which is the day the braces go.
 */
export function worthWaiting(ms: number, remaining: number): boolean {
  return usableDelay(ms) && ms <= remaining;
}

/**
 * The pause between two attempts, and it ENDS when the signal does.
 *
 * A real awaited pause rather than a resolved promise, even at a millisecond:
 * hammering an upstream that just answered 503 is what the delay exists to
 * prevent.
 *
 * Both sides are detached on the way out, whichever of them settled it. A timer
 * left pending keeps the process alive for the rest of the wait -- the seconds
 * still to run on a `Retry-After` the caller just abandoned -- and a listener
 * left on the caller's own signal outlives the call that added it.
 */
export function sleep(ms: number, signal: AbortSignal): Promise<void> {
  // An already-aborted signal fires no event, so a listener alone would wait out
  // a pause that was over before it began (SI3).
  if (signal.aborted) return Promise.resolve();

  return new Promise<void>(resolve => {
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', wake);
      resolve();
    }, ms);

    const wake = (): void => {
      clearTimeout(timer);
      resolve();
    };
    signal.addEventListener('abort', wake, { once: true });
  });
}
